import { Add, CheckCircleRounded, ErrorRounded, PictureAsPdf } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Button, CircularProgress, ListItemText, Stack, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  setInspectionSearchValue,
  setInspectionSelectedItem,
  setInspectionSortSelectedItem,
  setTemplateSearchValue,
} from 'app/actions/checklist';
import { FilterButton, IconButton, InputSearch, Scrollbar } from 'app/components';
import Table from 'app/components/Table';
import { FilterIcon } from 'app/icons';
import {
  ChecklistInspectionFilterDialog,
  ChecklistInspectionView,
  ChecklistTemplateDialog,
  ChecklistTemplateList,
  ChecklistTemplateView,
} from 'app/pages/Checklist';
import { invokeObjectUrl, useAsyncAction, usePermission, useRoles } from 'app/services';
import { downloadDamageReport, downloadPdf, inspectionLoad, templateLoad } from 'app/store/checklist';
import { autocolunItemsLoad, divisionItemsLoad } from 'app/store/transport';
import ExcelJS from 'exceljs';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const ChecklistInspectionColumns = (column, data, pdfPermission) => {
  const downloadHandler = (inspection_id) => {
    return async () => {
      const response = await downloadPdf(inspection_id);
      const inspectionDate = new Date(data.created_at).toISOString().split('T')[0];
      const pdfName = `Акт осмотра ТС ${data.number}_${data.trailer}_${inspectionDate}.pdf`;

      const blob = new Blob([response.data], { type: 'application/pdf' });
      invokeObjectUrl(blob, pdfName);
    };
  };

  return (
    <ListItemText
      key={column.name}
      sx={{
        width: column.width,
        ...(column.name === 'status' && {
          justifyContent: 'center',
          '.MuiTypography-root': {
            display: 'flex',
          },
        }),
      }}>
      {/* Кнопку скачивания pdf отображаем только при наличии пермишена pdf и для шаблона 27 */}
      {column.name === 'download' && data.template_id == 27 && pdfPermission && <PictureAsPdf onClick={downloadHandler(data.id)} />}
      {column.name === 'status' ? (
        data.status ? (
          <CheckCircleRounded fontSize='small' color='success' />
        ) : (
          <ErrorRounded fontSize='small' color='error' />
        )
      ) : (
        <Typography>{column.format ? column.format(data[column.name]) : data[column.name]}</Typography>
      )}
    </ListItemText>
  );
};

const inspectionTableColumns = config.clientUi === 'voykan' ? config.inspectionColumnsVoykan : config.inspectionColumns;

const ChecklistInspectionFilterItems = (item, filters = []) => {
  const checkFilter = (filterName) => {
    const filter = filters[filterName];
    switch (filterName) {
      case 'status':
      case 'template_id':
      case 'employee_id':
      case 'transport_id':
        return filter.length === 0 || filter.includes(item[filterName]);
      case 'transport_group_id':
        return filter.length === 0 || (item.transport_group_id != null && filter.includes(Number(item.transport_group_id)));
      case 'autocolon_id':
        if (filter.length === 0) return true;
        return (item.autocolon_ids || []).some((id) => filter.includes(Number(id)));
      case 'from':
        return !filter || moment(item.created_at).isSameOrAfter(filter);
      case 'to':
        return !filter || moment(item.created_at).isSameOrBefore(filter);
      default:
        return true;
    }
  };

  return Object.keys(filters).every((filterName) => checkFilter(filterName));
};

const Checklist = () => {
  const dispatch = useDispatch();
  const editPermission = useRoles([config.userRoles['ADMIN_CHECKLIST']]);
  const pdfPermission = usePermission(['pdf']);
  const {
    inspectionItems,
    inspectionSearchValue,
    inspectionSelectedFilters,
    inspectionSelectedFiltersLength,
    inspectionSortSelectedItem,
    inspectionSelectedItem,
  } = useSelector((state) => state.checklist);
  const { templateItems, templateSearchValue } = useSelector((state) => state.checklist);
  const [tab, setTab] = useState('inspection');
  const [fetchTab, setFetchTab] = useState([false, false]);
  const [anchorInspectorFilterEl, setAnchorInspectorFilterEl] = useState(null);

  const {
    asyncAction: fetchInspectionData,
    isLoading: isFetchInspectionLoading,
    isSuccessful: isFetchInspectionSuccessful,
  } = useAsyncAction(inspectionLoad);
  const {
    asyncAction: fetchTemplateData,
    isLoading: isFetchTemplateLoading,
    isSuccessful: isFetchTemplateSuccessful,
  } = useAsyncAction(templateLoad);

  const { asyncAction: fetchAutocolonData, isSuccessful: isFetchAutocolonSuccessful } = useAsyncAction(autocolunItemsLoad);
  const { asyncAction: fetchDivisionData, isSuccessful: isFetchDivisionSuccessful } = useAsyncAction(divisionItemsLoad);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const inspectionItemsFiltered = useMemo(() => {
    return inspectionItems.filter((item) => ChecklistInspectionFilterItems(item, inspectionSelectedFilters));
  }, [inspectionItems, inspectionSelectedFilters]);
  const handleInspectionSearchChange = (value) => dispatch(setInspectionSearchValue(value));

  const handleTemplateSearchChange = (value) => dispatch(setTemplateSearchValue(value));

  const handleInspectionSortChange = (value) => dispatch(setInspectionSortSelectedItem(value));

  const handleInspectionItemClick = (value) => dispatch(setInspectionSelectedItem(value));
  const handleDownloadClick = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Inspections', {
      pageSetup: {
        orientation: 'landscape',
        paperSize: 9,
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
      },
    });

    const headerRow = worksheet.addRow([
      'Наименование марки, модели ТС',
      'Гос. номер ТС',
      'ФИО проверяющего',
      'Дата и время контроля',
      'Показания одометра',
      'Статус',
      'Подпись',
    ]);

    inspectionItemsFiltered.forEach((item) => {
      const dataRow = worksheet.addRow([
        item.model_id || '',
        item.number || '',
        item.employee || '',
        moment(item.created_at).format('DD.MM.YYYY HH:mm'),
        (item.results && item.results[2] ? item.results[2] : '').toString(),
        item.status ? 'Пройден' : 'Не пройден',
        '',
      ]);

      dataRow.eachCell((cell) => {
        cell.alignment = {
          wrapText: true,
          vertical: 'top',
          horizontal: 'left',
        };
      });
    });

    worksheet.columns = [
      {
        header: 'Наименование марки, модели ТС',
        key: 'model',
        width: 40,
      },
      { header: 'Гос. номер ТС', key: 'number', width: 20 },
      { header: 'ФИО проверяющего', key: 'employee', width: 30 },
      { header: 'Дата и время контроля', key: 'date', width: 25 },
      { header: 'Показания одометра', key: 'odometer', width: 20 },
      { header: 'Статус', key: 'status', width: 15 },
      { header: 'Подпись', key: 'signature', width: 25 },
    ];

    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: 'center',
        vertical: 'middle',
        wrapText: true,
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFFFFF' },
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
      }
    });

    worksheet.eachRow((row) => {
      row.alignment = { wrapText: true };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inspections_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
  };

  const handleDownloadDamageReport = () => {
    downloadDamageReport();
  };

  useEffect(() => {
    const [inspectionTab, templateTab] = fetchTab;

    // Если пользователь пытается перейти на вкладку template без прав, перенаправляем на inspection
    if (tab === 'template' && !editPermission) {
      setTab('inspection');
      return;
    }

    switch (tab) {
      case 'inspection':
        setFetchTab([true, templateTab]);
        return !isFetchInspectionSuccessful && fetchInspectionData();
      case 'template':
        setFetchTab([inspectionTab, true]);

        if (!isFetchAutocolonSuccessful) {
          fetchAutocolonData();
        }
        if (!isFetchDivisionSuccessful) {
          fetchDivisionData();
        }
        return !isFetchTemplateSuccessful && fetchTemplateData();
    }
  }, [tab, editPermission]);

  return (
    <Stack
      direction='row'
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '660px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          transition: 'transform 300ms',
          boxShadow: '0px 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
        })}>
        <TabContext value={tab}>
          <Stack
            direction='row'
            sx={(theme) => ({
              width: '100%',
              position: 'relative',
              '.MuiTabs-root': {
                width: '100%',
                minHeight: '44px',
              },
              '.MuiTabs-flexContainer': {
                borderColor: theme.palette.divider,
                bgcolor: 'background.default',
                ...(tab !== 'transport' && {
                  borderColor: 'transparent',
                }),
              },
              '.MuiTab-root': {
                minHeight: '44px',
                fontSize: '12px',
                flex: '1 1 0px',
                '&.Mui-selected': {
                  color: 'inherit',
                  bgcolor: 'background.paper',
                },
                '&.Mui-selected:not(.Mui-selected)': {
                  color: theme.palette.text.secondary,
                },
              },
            })}>
            <TabList variant='fullWidth' onChange={(event, value) => setTab(value)}>
              <Tab value='inspection' label='Осмотры' disableRipple />
              {editPermission && <Tab value='template' label='Шаблоны' disableRipple />}
            </TabList>
          </Stack>
          <Stack
            direction='row'
            sx={(theme) => ({
              flex: '1 1 auto',
              display: 'flex',
              overflow: 'hidden',
              '.MuiTabPanel-root': {
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: theme.spacing(0),
              },
              '.MuiTabPanel-root[hidden]': {
                display: 'none',
              },
            })}>
            <TabPanel value='inspection' sx={{ position: 'relative' }}>
              {!isFetchInspectionLoading && fetchTab[0] && inspectionItems.length !== 0 && (
                <React.Fragment>
                  <Stack
                    direction='row'
                    alignItems='center'
                    sx={(theme) => ({
                      padding: theme.spacing(1),
                      '.MuiInputBase-root': {
                        padding: '6px 8px',
                        marginRight: theme.spacing(1),
                      },
                      '.MuiButton-endIcon > svg': {
                        fontSize: '16px',
                        svg: {
                          paddingLeft: '2px',
                        },
                      },
                    })}>
                    <InputSearch
                      placeholder={config.clientUi === 'voykan' ? 'Бригада или сотрудник' : 'Госномер или сотрудник'}
                      value={inspectionSearchValue}
                      onChange={handleInspectionSearchChange}
                    />
                    <FilterButton
                      label='Фильтры'
                      badgeContent={inspectionSelectedFiltersLength}
                      ref={anchorInspectorFilterEl}
                      active={!!anchorInspectorFilterEl}
                      onClick={(event) => setAnchorInspectorFilterEl(event.currentTarget)}
                      endIcon={<FilterIcon width={16} height={16} />}
                    />
                    <Button
                      variant='contained'
                      disableElevation
                      onClick={handleDownloadClick}
                      sx={(theme) => ({
                        position: 'relative',
                        minWidth: 'auto',
                        color: theme.palette.text.secondary,
                        padding: '6px 10px',
                        height: '100%',
                        bgcolor: 'background.default',
                        borderWidth: '0',
                        borderStyle: 'solid',
                        borderColor: theme.palette.divider,
                        textTransform: 'initial',
                        gap: '4px',
                        marginLeft: '6px',
                        span: {
                          margin: theme.spacing(0),
                        },
                        path: {
                          fill: theme.palette.text.secondary,
                        },
                        '&:hover, &.active': {
                          bgcolor: 'background.default',
                        },
                        '&.Mui-disabled': {
                          bgcolor: alpha(theme.palette.text.secondary, 0.05),
                        },
                      })}>
                      Скачать
                    </Button>
                    <Button
                      variant='contained'
                      disableElevation
                      onClick={handleDownloadDamageReport}
                      sx={(theme) => ({
                        position: 'relative',
                        minWidth: 'auto',
                        color: theme.palette.text.secondary,
                        padding: '6px 10px',
                        height: '100%',
                        bgcolor: 'background.default',
                        borderWidth: '0',
                        borderStyle: 'solid',
                        borderColor: theme.palette.divider,
                        textTransform: 'initial',
                        gap: '4px',
                        marginLeft: '6px',
                        span: {
                          margin: theme.spacing(0),
                        },
                        '& .MuiSvgIcon-root': {
                          fill: theme.palette.text.secondary,
                        },
                        '&:hover, &.active': {
                          bgcolor: 'background.default',
                          '& .MuiSvgIcon-root': {
                            fill: theme.palette.text.secondary,
                          },
                        },
                        '&.Mui-disabled': {
                          bgcolor: alpha(theme.palette.text.secondary, 0.05),
                        },
                      })}
                      startIcon={<AssessmentIcon />}
                    />
                    <ChecklistInspectionFilterDialog open={!!anchorInspectorFilterEl} onClose={() => setAnchorInspectorFilterEl(null)} />
                  </Stack>
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    sx={{
                      height: '100%',
                      overflow: 'hidden',
                    }}>
                    <Table
                      columns={inspectionTableColumns}
                      customColumnsRender={(column, data) => ChecklistInspectionColumns(column, data, pdfPermission)}
                      showColumns={inspectionTableColumns.map((column) => column.name)}
                      rows={inspectionItemsFiltered}
                      selectedRow={inspectionSelectedItem}
                      searchValue={inspectionSearchValue}
                      searchValueKeys={
                        config.clientUi === 'voykan'
                          ? ['number', 'employee', 'transport_group_name', 'autocolon_name']
                          : ['number', 'employee', 'group_name']
                      }
                      storageColumnsWidthKey='inspectionTable'
                      sortSelectedKey={inspectionSortSelectedItem}
                      onRowClick={handleInspectionItemClick}
                      onSortChange={handleInspectionSortChange}
                    />
                    {/* <ChecklistInspectionList/> */}
                  </Stack>
                </React.Fragment>
              )}
              {!isFetchInspectionLoading && fetchTab[0] && inspectionItems.length === 0 && (
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  sx={(theme) => ({
                    width: '100%',
                    height: '100%',
                    padding: theme.spacing(2),
                  })}>
                  <Typography align='center' variant='body1' component='span'>
                    Здесь пока ничего нет
                  </Typography>
                  <Typography align='center' color='text.secondary' variant='caption' component='span'>
                    В этом разделе будет находиться список осмотров
                  </Typography>
                </Stack>
              )}
              {(isFetchInspectionLoading || !fetchTab[0]) && (
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  sx={(theme) => ({
                    width: '100%',
                    height: '100%',
                    padding: theme.spacing(2),
                  })}>
                  <CircularProgress size={24} />
                </Stack>
              )}
            </TabPanel>
            {editPermission && (
              <TabPanel value='template'>
                {!isFetchTemplateLoading && fetchTab[1] && templateItems.length !== 0 && (
                  <React.Fragment>
                    <Stack
                      direction='row'
                      alignItems='center'
                      sx={(theme) => ({
                        padding: theme.spacing(1),
                        '.MuiInputBase-root': {
                          padding: '6px 8px',
                        },
                        '.MuiButton-endIcon > svg': {
                          fontSize: '16px',
                          svg: {
                            paddingLeft: '2px',
                          },
                        },
                      })}>
                      <InputSearch placeholder='Название' value={templateSearchValue} onChange={handleTemplateSearchChange} />
                      <IconButton onClick={() => setTemplateDialogOpen(true)} endIcon={<Add />} />
                      <ChecklistTemplateDialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} />
                    </Stack>
                    <Stack
                      direction='column'
                      alignItems='center'
                      justifyContent='center'
                      sx={{
                        height: '100%',
                        overflow: 'hidden',
                      }}>
                      <ChecklistTemplateList />
                    </Stack>
                  </React.Fragment>
                )}
                {!isFetchTemplateLoading && fetchTab[1] && templateItems.length === 0 && (
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    sx={(theme) => ({
                      width: '100%',
                      height: '100%',
                      padding: theme.spacing(2),
                    })}>
                    <Typography align='center' variant='body1' component='span'>
                      Здесь пока ничего нет
                    </Typography>
                    <Typography align='center' color='text.secondary' variant='caption' component='span'>
                      В этом разделе будет находиться список шаблонов для осмотров
                    </Typography>
                  </Stack>
                )}
                {(isFetchTemplateLoading || !fetchTab[1]) && (
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    sx={(theme) => ({
                      width: '100%',
                      height: '100%',
                      padding: theme.spacing(2),
                    })}>
                    <CircularProgress size={24} />
                  </Stack>
                )}
              </TabPanel>
            )}
          </Stack>
        </TabContext>
      </Stack>
      <Stack
        direction='column'
        sx={(theme) => ({
          height: '100%',
          flex: '1 1 auto',
          bgcolor: theme.palette.background.default,
          '.simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
          '.MuiTabPanel-root': {
            width: '100%',
            height: '100%',
            padding: 0,
          },
        })}>
        <Scrollbar>
          <TabContext value={tab}>
            <TabPanel value='inspection'>
              <ChecklistInspectionView />
            </TabPanel>
            {editPermission && (
              <TabPanel value='template'>
                <ChecklistTemplateView />
              </TabPanel>
            )}
          </TabContext>
        </Scrollbar>
      </Stack>
    </Stack>
  );
};

export default Checklist;
